import type { AuthBroadcast } from './broadcastSync';
import { authEnvironment, safeGet, safeRemove, safeSet, type AuthEnvironment } from './environment';
import type { AuthNamespace } from './namespace';

export type AuthMode = 'body' | 'cookie';

export type TokenChangeOrigin = 'login' | 'refresh' | 'logout' | 'broadcast';

export interface StoredTokens {
    accessToken: string | null;
    refreshToken: string | null;
}

/** Событие смены токенов — только access: refresh из хранилища наружу не выходит. */
export interface TokenChangeEvent {
    accessToken: string | null;
    origin: TokenChangeOrigin;
}

export type TokenChangeListener = (event: TokenChangeEvent) => void;

export interface TokenStoreOptions {
    namespace: Pick<AuthNamespace, 'accessTokenKey' | 'refreshTokenKey' | 'authModeKey'>;
    /** Рассылка соседним вкладкам; без неё смену токенов видит только эта вкладка. */
    broadcast?: Pick<AuthBroadcast, 'publish'>;
    env?: Partial<Pick<AuthEnvironment, 'storage'>>;
}

export interface TokenStore {
    /** Режим сервера; до первого `setMode` — из кэша localStorage (bootstrap до ответа SignInSettings). */
    getMode(): AuthMode;
    /** body→cookie переносит живой access в память и чистит токены в localStorage. */
    setMode(mode: AuthMode): void;
    getAccessToken(): string | null;
    /** В cookie-режиме refresh в httpOnly cookie — из JS невидим, всегда null. */
    getRefreshToken(): string | null;
    getTokens(): StoredTokens;
    /** Единственный писатель токенов: одна запись — одно событие подписчикам и одна рассылка (кроме `broadcast`). */
    setTokens(tokens: StoredTokens, origin: TokenChangeOrigin): void;
    /** `logout` — с рассылкой `logged-out`, `broadcast` — эхо выхода соседней вкладки, без рассылки. */
    clearTokens(origin: 'logout' | 'broadcast'): void;
    /**
     * Поколение сессии вкладки: растёт на каждом `clearTokens`. Ротация, начатая в прежнем поколении, свой результат
     * не пишет — иначе он пережил бы выход.
     */
    epoch(): number;
    subscribe(listener: TokenChangeListener): () => void;
}

const isAuthMode = (value: unknown): value is AuthMode => value === 'body' || value === 'cookie';

/**
 * Хранилище токенов. Body-режим (прежний поток, FrontVTB, MobileAPI): оба токена в localStorage. Cookie-режим: refresh
 * в httpOnly cookie сервера, access — в памяти экземпляра, localStorage токенов не содержит.
 */
export function createTokenStore({ namespace, broadcast, env }: TokenStoreOptions): TokenStore {
    const { accessTokenKey, refreshTokenKey, authModeKey } = namespace;
    const resolved = authEnvironment(env);
    const listeners = new Set<TokenChangeListener>();
    // режим читается при первом обращении, а не при создании: фабрика без эффектов
    let mode: AuthMode | null = null;
    let inMemoryAccessToken: string | null = null;
    let generation = 0;

    const get = (key: string) => safeGet(resolved.storage, key);
    const currentMode = (): AuthMode => {
        if (mode === null) {
            const cached = get(authModeKey);
            mode = isAuthMode(cached) ? cached : 'body';
        }
        return mode;
    };

    const notify = (event: TokenChangeEvent) => {
        listeners.forEach((listener) => {
            try {
                listener(event);
            } catch {
                // упавший подписчик не мешает остальным
            }
        });
    };

    const getAccessToken = () => (currentMode() === 'cookie' ? inMemoryAccessToken : get(accessTokenKey));
    const getRefreshToken = () => (currentMode() === 'cookie' ? null : get(refreshTokenKey));

    const clearTokens = (origin: 'logout' | 'broadcast') => {
        generation += 1;
        if (currentMode() === 'cookie') {
            // refresh-cookie чистит сервер на SignOut
            inMemoryAccessToken = null;
        } else {
            safeRemove(resolved.storage, accessTokenKey);
            safeRemove(resolved.storage, refreshTokenKey);
        }
        notify({ accessToken: null, origin });
        if (origin === 'logout') broadcast?.publish({ type: 'logged-out' });
    };

    const setTokens = (tokens: StoredTokens, origin: TokenChangeOrigin) => {
        if (currentMode() === 'cookie') {
            if (!tokens.accessToken) {
                clearTokens(origin === 'broadcast' ? 'broadcast' : 'logout');
                return;
            }
            if (inMemoryAccessToken === tokens.accessToken) return;
            inMemoryAccessToken = tokens.accessToken;
            notify({ accessToken: inMemoryAccessToken, origin });
            if (origin !== 'broadcast') {
                broadcast?.publish({ type: 'tokens-updated', accessToken: tokens.accessToken, refreshToken: '' });
            }
            return;
        }

        if (!tokens.accessToken && !tokens.refreshToken) {
            clearTokens(origin === 'broadcast' ? 'broadcast' : 'logout');
            return;
        }

        // Эхо соседней вкладки не пропускается и при совпадении с хранилищем: отправитель пишет в общий localStorage до
        // рассылки, а подписчики получателя (планировщик) без события не узнают о новом access.
        const sameAsStorage =
            get(accessTokenKey) === tokens.accessToken && get(refreshTokenKey) === tokens.refreshToken;
        if (sameAsStorage && origin !== 'broadcast') return;

        if (tokens.accessToken) safeSet(resolved.storage, accessTokenKey, tokens.accessToken);
        else safeRemove(resolved.storage, accessTokenKey);
        if (tokens.refreshToken) safeSet(resolved.storage, refreshTokenKey, tokens.refreshToken);
        else safeRemove(resolved.storage, refreshTokenKey);

        notify({ accessToken: getAccessToken(), origin });
        if (origin !== 'broadcast') {
            broadcast?.publish({
                type: 'tokens-updated',
                accessToken: tokens.accessToken ?? '',
                refreshToken: tokens.refreshToken ?? '',
            });
        }
    };

    return {
        getMode: currentMode,
        setMode(next) {
            const previous = currentMode();
            safeSet(resolved.storage, authModeKey, next);
            mode = next;
            if (next === 'cookie') {
                // Живой access переезжает в память, а не теряется: иначе смена режима (флаг сервера или body-fallback
                // при сбое выдачи refresh) рвёт сессию — стор «залогинен», а запросы без Authorization. Токены в
                // localStorage в cookie-режиме — лишняя поверхность XSS: их чистит и повторный cookie (их могла
                // записать вкладка, открытая ещё в body-режиме).
                if (previous !== next) inMemoryAccessToken = get(accessTokenKey) ?? inMemoryAccessToken;
                safeRemove(resolved.storage, accessTokenKey);
                safeRemove(resolved.storage, refreshTokenKey);
            } else if (previous !== next) {
                inMemoryAccessToken = null;
            }
        },
        getAccessToken,
        getRefreshToken,
        getTokens: () => ({ accessToken: getAccessToken(), refreshToken: getRefreshToken() }),
        setTokens,
        clearTokens,
        epoch: () => generation,
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
}
